import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { LoaderComponent } from '../../loader/loader.component';
import { CardsApiService } from '../../../../_services/cards-service/cards-api/cards-api.service';
import { Card, CardSdkConfig, CardStatus, CardType } from '../../../../_interfaces/cards.interface';
import { Subscription } from 'rxjs';
import { VirtualCardStaticComponent } from '../virtual-card-static/virtual-card-static.component';
import { environment } from '../../../../../environments/environment';
import { ToastService } from '../../../../_services/toast-service/toast.service';

@Component({
  selector: 'app-virtual-card',
  standalone: true,
  imports: [LoaderComponent, VirtualCardStaticComponent, NgClass],
  templateUrl: './virtual-card-iframe.component.html',
})
export class VirtualCardIframeComponent implements OnInit, OnDestroy {
  readonly toastService = inject(ToastService);

  // listen to messages from the iframe
  @HostListener('window:message', ['$event'])
  onWebMessage(event: MessageEvent) {
    // Only process messages from the trusted origin
    if (!this.config()?.url.includes(event.origin)) {
      return;
    }

    // stop loading view on the last event supplied. Adding 500ms buffer, as this still shows too early.
    // 2003 === LOADER_OVERLAY_REMOVED
    if (event.data?.code === 2003) {
      setTimeout(() => {
        this.isLoadingCard.set(false);
      }, 500);
    }

    if (
      event.data?.type === 'error' ||
      event.data?.status === 'failure' ||
      (event.data?.code && event.data?.code >= 4000)
    ) {
      this.handleIframeInternalError();
    }
  }

  private readonly dashboardBaseUrl = environment.dashboardBaseUrl;
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cardApiService = inject(CardsApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly randomInt = Math.floor(Math.random() * 10 ** 16); //NOSONAR this is required
  subscriptions = new Subscription();

  cardType = input.required<CardType>();

  cardDetailEmpty = computed(() => {
    return {} as Card;
  });

  cardDetailFailed = computed(() => {
    return { status: CardStatus.Removed } as Card;
  });

  iframeEl = viewChild<ElementRef<HTMLIFrameElement>>('iframe');
  config = signal<CardSdkConfig | null>(null);
  isLoading = signal<boolean>(true);
  isLoadingCard = signal<boolean>(true);
  hasError = signal<boolean>(false);
  iframeSrc = computed<SafeResourceUrl | null>(() => {
    const conf = this.config();
    const dataStr = this.iframeDataStr();
    if (!conf || !dataStr) return null;

    const fullUrl = `${conf.url}?var=${this.randomInt}&data=${dataStr}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl); // NOSONAR: URL constructed from trusted backend config for PCI-compliant card provider iframe
  });
  private readonly iframeDataStr = computed(() => {
    const c = this.config();
    if (!c) return undefined;
    const data = {
      apiKeyId: c.key_id,
      apiKey: c.key,
      cardId: c.card_id,
      uniqueToken: c.otp,
      clickToCopy: true,
      showCopy: true,
      loadDefaultStyle: false,
      loader: 'bars',
      showPreloadedData: true,
      fieldNames:
        '{"emboss": "Card holder", "exp": "Expires", "cvv": "CVC", "details": "Show Card Details", "clickToCopyCopied": "Copied to clipboard" }',
      styleUrl: this.dashboardBaseUrl + 'assets/css/mea-wallet-card-styles.css',
    };
    return btoa(JSON.stringify(data));
  });

  constructor() {
    // Angular does not accept the iframe attribute "allow" to be dynamic, so we need to trick it with this function
    // to change the attribute to include the dynamic url when the loader disappears and the iframe renders
    effect(() => {
      const config = this.config();
      const iframeRef = this.iframeEl();
      if (config && iframeRef) {
        // It's important for Chrome to have an existing static attribute allow="clipboard-write" in the template before we set the dynamic url.
        iframeRef.nativeElement.setAttribute('allow', `clipboard-write ${config.url}`);
      }
    });
  }

  ngOnInit() {
    this.loadCardData();
    this.subscriptions.add(
      this.route.params
        .pipe(
          map((params) => params['id']),
          filter((id) => !!id),
          tap(() => {
            this.isLoading.set(true);
            this.hasError.set(false);
            this.config.set(null);
          }),
          switchMap((id) => this.cardApiService.getCardSdkConfig(id, this.cardType())),
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.config.set(data);
              this.isLoading.set(false);
            }
          },
          error: () => {
            this.hasError.set(true);
            this.isLoading.set(false);
          },
        }),
    );
  }

  private loadCardData() {
    this.subscriptions.add(
      this.route.params
        .pipe(
          map((params) => params['id']),
          filter((id) => !!id),
          // Ensure we reset states on every new load attempt
          tap(() => {
            this.isLoading.set(true);
            this.isLoadingCard.set(true); // Ensure the iframe loader shows
            this.hasError.set(false);
            this.config.set(null);
          }),
          switchMap((id) => this.cardApiService.getCardSdkConfig(id, this.cardType())),
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.config.set(data);
              this.isLoading.set(false);
            } else {
              this.handleError();
            }
          },
          error: () => this.handleError(),
        }),
    );
  }

  reloadCardData() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.loadCardData();
  }

  private handleIframeInternalError() {
    this.handleError();
    this.isLoadingCard.set(false);
  }

  private handleError() {
    this.hasError.set(true);
    this.isLoading.set(false);
    this.toastService.show('Could not fetch card details.', 'iconoir-xmark');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
